import { useEffect, useState } from "react";
import { AppShell, PageHeader } from "../components/AppShell";
import {
    BookOpen,
    Rocket,
    Wheat,
    ChefHat,
    ShoppingBasket,
    PackageCheck,
    Truck,
    BarChart3,
    CreditCard,
    User,
    HelpCircle,
    AlertTriangle,
    CheckCircle2,
    Sparkles,
    Zap,
    Crown,
    ArrowRight,
    FileText,
    Upload,
    Scale,
    ShoppingCart,
    Layers,
    Infinity as InfinityIcon,
    Shield,
} from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Pagina guida / manuale utente dettagliato di RENOVA360 Food Cost Manager.
 * Struttura:
 *  - Indice sticky a sinistra con scroll-spy
 *  - Sezioni didattiche con casi d'uso, passo-passo, note ed errori comuni
 */

const SECTIONS = [
    { id: "intro", label: "Introduzione", icon: Rocket },
    { id: "login", label: "Primo accesso", icon: Shield },
    { id: "dashboard", label: "Dashboard", icon: BarChart3 },
    { id: "ingredienti", label: "Ingredienti", icon: Wheat },
    { id: "piatti", label: "Piatti & Ricette", icon: ChefHat },
    { id: "catalogo", label: "Catalogo globale", icon: BookOpen },
    { id: "ricetta-ai", label: "Ricetta dettagliata AI", icon: Sparkles },
    { id: "menu-import", label: "Import menu PDF (AI)", icon: Upload },
    { id: "acquisti", label: "Acquisti & OCR fatture", icon: ShoppingBasket },
    { id: "fornitori", label: "Fornitori & listini", icon: Truck },
    { id: "ordini", label: "Ordini intelligenti", icon: PackageCheck },
    { id: "lista-mancanti", label: "Lista mancanti", icon: ShoppingCart },
    { id: "statistiche", label: "Statistiche", icon: BarChart3 },
    { id: "billing", label: "Piani & quote AI", icon: CreditCard },
    { id: "profilo", label: "Profilo", icon: User },
    { id: "casi-specifici", label: "Casi specifici", icon: AlertTriangle },
    { id: "faq", label: "FAQ", icon: HelpCircle },
];

export default function Guide() {
    const [active, setActive] = useState("intro");

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) setActive(entry.target.id);
                });
            },
            { rootMargin: "-20% 0px -60% 0px", threshold: 0 }
        );
        SECTIONS.forEach((s) => {
            const el = document.getElementById(s.id);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, []);

    return (
        <AppShell>
            <PageHeader
                eyebrow="Manuale utente"
                title="Guida completa al portale"
                subtitle="Tutto quello che RENOVA360 Food Cost Manager può fare — con esempi pratici, passo-passo e casi specifici."
            />

            <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8 mt-4">
                {/* Indice sticky */}
                <aside className="lg:sticky lg:top-6 self-start print:hidden">
                    <div className="bg-paper rounded-xl border border-brand-line p-3 shadow-card">
                        <div className="eyebrow text-brand-gold px-2 mb-2">Indice</div>
                        <nav className="flex flex-col gap-0.5" data-testid="guide-toc">
                            {SECTIONS.map(({ id, label, icon: Icon }) => (
                                <a
                                    key={id}
                                    href={`#${id}`}
                                    data-testid={`guide-toc-${id}`}
                                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[13px] transition ${
                                        active === id
                                            ? "bg-brand-gold/15 text-brand-navy font-semibold"
                                            : "text-brand-ink/70 hover:text-brand-ink hover:bg-brand-cream"
                                    }`}
                                >
                                    <Icon size={13} strokeWidth={active === id ? 2.4 : 1.8} />
                                    <span className="truncate">{label}</span>
                                </a>
                            ))}
                        </nav>
                    </div>
                </aside>

                <main className="space-y-12 min-w-0" data-testid="guide-content">
                    <IntroSection />
                    <LoginSection />
                    <DashboardSection />
                    <IngredientsSection />
                    <RecipesSection />
                    <CatalogSection />
                    <RecipeAISection />
                    <MenuImportSection />
                    <PurchasesSection />
                    <SuppliersSection />
                    <OrdersSection />
                    <MissingListSection />
                    <StatsSection />
                    <BillingSection />
                    <ProfileSection />
                    <CasesSection />
                    <FAQSection />

                    <div className="pt-8 border-t border-brand-line text-center">
                        <p className="text-[12px] text-brand-muted">
                            Documentazione aggiornata · RENOVA360 Food Cost Manager
                        </p>
                        <Link
                            to="/dashboard"
                            className="mt-3 inline-flex items-center gap-2 rounded-full bg-brand-navy text-white px-5 py-2 text-sm font-semibold hover:bg-brand-navyLight transition"
                        >
                            <ArrowRight size={14} /> Torna alla dashboard
                        </Link>
                    </div>
                </main>
            </div>
        </AppShell>
    );
}

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------
function Section({ id, icon: Icon, title, eyebrow, children }) {
    return (
        <section id={id} className="scroll-mt-20">
            <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-full bg-brand-gold/15 text-brand-gold flex items-center justify-center shrink-0">
                    <Icon size={18} />
                </div>
                <div>
                    {eyebrow && <div className="eyebrow text-brand-gold">{eyebrow}</div>}
                    <h2 className="font-display text-3xl text-brand-ink leading-tight">
                        {title}
                    </h2>
                </div>
            </div>
            <div className="bg-paper rounded-2xl border border-brand-line shadow-card p-6 sm:p-7 space-y-5">
                {children}
            </div>
        </section>
    );
}

function Step({ n, title, children }) {
    return (
        <div className="flex gap-4">
            <div className="h-8 w-8 rounded-full bg-brand-navy text-white flex items-center justify-center font-display text-sm shrink-0">
                {n}
            </div>
            <div className="flex-1 min-w-0">
                <div className="font-semibold text-brand-ink text-[15px] mb-1">{title}</div>
                <div className="text-[14px] text-brand-ink/80 leading-relaxed space-y-2">
                    {children}
                </div>
            </div>
        </div>
    );
}

function Callout({ tone = "info", title, children }) {
    const tones = {
        info: "bg-brand-cream border-brand-line text-brand-ink",
        success: "bg-emerald-50 border-emerald-200 text-emerald-900",
        warning: "bg-amber-50 border-amber-300 text-amber-900",
        danger: "bg-red-50 border-red-200 text-brand-danger",
        gold: "bg-brand-gold/10 border-brand-gold/40 text-brand-navy",
    };
    const icons = {
        info: <HelpCircle size={16} />,
        success: <CheckCircle2 size={16} />,
        warning: <AlertTriangle size={16} />,
        danger: <AlertTriangle size={16} />,
        gold: <Sparkles size={16} />,
    };
    return (
        <div className={`rounded-xl border px-4 py-3 flex items-start gap-3 ${tones[tone]}`}>
            <span className="shrink-0 mt-0.5">{icons[tone]}</span>
            <div className="flex-1 text-[13px] leading-relaxed">
                {title && <div className="font-bold mb-0.5">{title}</div>}
                {children}
            </div>
        </div>
    );
}

function KeyRow({ k, v }) {
    return (
        <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3 text-[13px]">
            <div className="text-[10px] uppercase tracking-[0.16em] text-brand-muted font-bold sm:w-32 shrink-0">
                {k}
            </div>
            <div className="text-brand-ink/85 flex-1">{v}</div>
        </div>
    );
}

function Bullet({ children }) {
    return (
        <li className="flex items-start gap-2 text-[14px] text-brand-ink/85 leading-relaxed">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-gold shrink-0 mt-2" />
            <span>{children}</span>
        </li>
    );
}

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------
function IntroSection() {
    return (
        <Section id="intro" icon={Rocket} title="Benvenuto in RENOVA360" eyebrow="Introduzione">
            <p className="text-[14px] text-brand-ink/85 leading-relaxed">
                RENOVA360 Food Cost Manager è la tua cabina di regia per calcolare e controllare
                il costo dei piatti, ottimizzare gli acquisti, scegliere il fornitore migliore
                per ogni ingrediente e mantenere il food cost sotto controllo mese dopo mese.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Callout tone="gold" title="Cosa ottieni">
                    Food cost sempre aggiornato, margini visibili, ordini intelligenti,
                    OCR fatture e import menu PDF via AI.
                </Callout>
                <Callout tone="info" title="Per chi è">
                    Ristoratori, chef, cost controller e responsabili acquisti di ristoranti
                    italiani — piccoli e medi.
                </Callout>
            </div>
            <div>
                <div className="font-semibold text-brand-ink text-[15px] mb-2">
                    Le 7 aree del portale
                </div>
                <ul className="space-y-1.5">
                    <Bullet>
                        <strong>Dashboard</strong>: panoramica veloce su ingredienti, ricette,
                        food cost medio e ultimi acquisti.
                    </Bullet>
                    <Bullet>
                        <strong>Ingredienti</strong>: la tua dispensa, con prezzi, scarti,
                        fornitori e categorie.
                    </Bullet>
                    <Bullet>
                        <strong>Piatti</strong>: ricette con calcolo automatico del food cost e
                        prezzo di vendita suggerito.
                    </Bullet>
                    <Bullet>
                        <strong>Acquisti</strong>: registra DDT e fatture (anche via OCR
                        automatico) e aggiorna i costi.
                    </Bullet>
                    <Bullet>
                        <strong>Fornitori</strong>: anagrafica fornitori con listini prezzi.
                    </Bullet>
                    <Bullet>
                        <strong>Ordini</strong>: carrello intelligente che sceglie il fornitore
                        migliore per ogni riga (feature Pro).
                    </Bullet>
                    <Bullet>
                        <strong>Statistiche</strong>: approfondimenti su food cost, margini e
                        acquisti nel tempo.
                    </Bullet>
                </ul>
            </div>
        </Section>
    );
}

function LoginSection() {
    return (
        <Section id="login" icon={Shield} title="Primo accesso & account" eyebrow="Iniziare">
            <Step n={1} title="Login con Google">
                <p>
                    Vai su <code>/login</code> e clicca <strong>Continua con Google</strong>.
                    L'accesso è federato tramite Emergent Auth: nessuna password da ricordare.
                </p>
            </Step>
            <Step n={2} title="Trial Pro di 5 giorni">
                <p>
                    Al primo login ricevi <strong>5 giorni di prova gratuiti del piano Pro</strong>
                    , con tutte le funzioni sbloccate (ordini intelligenti, quote AI maggiorate).
                </p>
            </Step>
            <Step n={3} title="Seed automatico della dispensa">
                <p>
                    Subito dopo il login trovi <strong>253 ingredienti italiani già inseriti</strong>
                    {" "}nella tua dispensa, con categorie, unità di misura coerenti e
                    scarto suggerito. Puoi modificare prezzi, nomi o eliminarli.
                </p>
            </Step>
            <Callout tone="info" title="Dati sempre privati">
                Ogni account ha i propri ingredienti, ricette, fornitori e acquisti.
                Nessun dato viene condiviso fra ristoranti.
            </Callout>
        </Section>
    );
}

function DashboardSection() {
    return (
        <Section id="dashboard" icon={BarChart3} title="Dashboard" eyebrow="Panoramica rapida">
            <p className="text-[14px] text-brand-ink/85">
                La dashboard mostra a colpo d'occhio lo stato del ristorante.
            </p>
            <div className="space-y-2">
                <KeyRow k="KPI in alto" v="Totale ingredienti, totale piatti, food cost medio, spesa acquisti del mese." />
                <KeyRow k="Top 5 piatti costosi" v="Le ricette con food cost% più alto: candidati naturali a revisione dose o prezzo di listino." />
                <KeyRow k="Ultimi 5 acquisti" v="Comodo per ricordarti cosa hai inserito di recente." />
            </div>
            <Callout tone="gold" title="Suggerimento">
                Usa la Dashboard come prima fermata di ogni mattina: 30 secondi ti bastano per
                capire se qualcosa sta andando fuori linea.
            </Callout>
        </Section>
    );
}

function IngredientsSection() {
    return (
        <Section id="ingredienti" icon={Wheat} title="Ingredienti" eyebrow="Dispensa">
            <p className="text-[14px] text-brand-ink/85">
                Qui gestisci prezzi, scarto, fornitore e stock di ogni materia prima.
                I prezzi da qui alimentano <strong>tutti</strong> i calcoli di food cost.
            </p>
            <Step n={1} title="Aggiungere un ingrediente">
                <p>
                    Pulsante <strong>Aggiungi</strong> → nome, categoria (12 disponibili),
                    unità di misura, costo unitario, scarto %, stock iniziale e fornitore
                    abituale.
                </p>
            </Step>
            <Step n={2} title="Regola d'oro sull'unità di misura">
                <p>
                    Usa <strong>kg / g</strong> per i pesi, <strong>l / ml</strong> per i
                    liquidi. L'unità <code>pz</code> è <strong>consentita solo per le
                    bevande</strong> (acqua, vino, birra in bottiglia). Per qualsiasi altro
                    alimento non è ammessa perché un peso variabile (uovo, avocado) falsa il
                    food cost.
                </p>
                <Callout tone="warning" title="Migrazione automatica da pz">
                    Se la tua dispensa ha ingredienti non-bevande in <code>pz</code>, vedrai il
                    pulsante <strong>Correggi N a peso</strong>. Premendolo il sistema converte
                    tutti gli ingredienti in grammi usando una tabella di pesi medi realistici
                    (uovo 55 g, limone 100 g, avocado 180 g…) e aggiorna automaticamente tutte
                    le ricette che li usavano.
                </Callout>
            </Step>
            <Step n={3} title="Scarto (waste %)">
                <p>
                    Lo scarto è la percentuale di materia prima che finisce nel cestino. Il
                    sistema calcola la quantità "effettiva" come
                    {" "}<code>qty / (1 − waste/100)</code>.
                </p>
                <p>
                    Esempio: 200 g di carciofi netti con scarto 40% pesano in realtà
                    {" "}<code>200/(1−0.40) = 333 g</code> lordi. Il costo è calcolato sui 333 g,
                    non sui 200.
                </p>
            </Step>
            <Step n={4} title="Auto-conversione delle unità">
                <p>
                    Quando una quantità supera <strong>1000 g</strong> o <strong>1000 ml</strong>,
                    il portale la mostra automaticamente in <strong>kg / l</strong> nell'UI, così
                    eviti zeri in più. I dati in DB restano coerenti.
                </p>
            </Step>
            <Step n={5} title="Categorie disponibili">
                <p>
                    Carne, Pesce, Verdure, Frutta, Latticini, Farine, Cereali, Oli, Spezie,
                    Bevande, Dispensa, Dolci. Le categorie guidano il colore delle card e i
                    raggruppamenti nelle statistiche.
                </p>
            </Step>
            <Step n={6} title="Eliminazione e reset">
                <p>
                    Da ogni scheda ingrediente trovi il pulsante <strong>Elimina</strong> (con
                    dialog di conferma). In testa alla pagina c'è anche <strong>Reset dispensa</strong>
                    {" "}per cancellare tutto (non reversibile).
                </p>
            </Step>
        </Section>
    );
}

function RecipesSection() {
    return (
        <Section id="piatti" icon={ChefHat} title="Piatti & Ricette" eyebrow="Menu">
            <p className="text-[14px] text-brand-ink/85">
                La pagina Piatti è il cuore del portale: qui componi ogni ricetta, vedi il food
                cost in tempo reale e confronti il prezzo di listino col prezzo suggerito.
            </p>
            <Step n={1} title="Creare una ricetta">
                <p>Pulsante <strong>+</strong> (o FAB su mobile) → si apre il builder.</p>
                <ul className="space-y-1.5 mt-1">
                    <Bullet>Nome, categoria (Antipasti, Primi, Secondi, Contorni, Pizze, Dolci, Bevande).</Bullet>
                    <Bullet>Porzioni di base: è il numero di porzioni a cui si riferiscono le dosi.</Bullet>
                    <Bullet>Prezzo di vendita IVA esclusa: serve per calcolare il food cost %.</Bullet>
                    <Bullet>Elenco ingredienti con quantità e unità (il portale normalizza automaticamente in g/ml).</Bullet>
                </ul>
            </Step>
            <Step n={2} title="Food cost & prezzo suggerito">
                <p>
                    Per ogni piatto vedi:
                </p>
                <ul className="space-y-1.5 mt-1">
                    <Bullet><strong>Costo/porzione</strong> = somma di (quantità × costo unitario × fattore scarto) / porzioni.</Bullet>
                    <Bullet><strong>Food cost %</strong> = costo/porzione ÷ prezzo di listino × 100.</Bullet>
                    <Bullet><strong>Margine</strong> = prezzo di listino − costo/porzione.</Bullet>
                    <Bullet><strong>Prezzo suggerito</strong> = costo/porzione ÷ 0,30 (target FC 30%).</Bullet>
                </ul>
                <Callout tone="success" title="Semaforo food cost">
                    Verde &lt; 30% · Ambra 30-35% · Rosso &gt; 35%. Ad un colpo d'occhio
                    vedi quali piatti sono fuori linea.
                </Callout>
            </Step>
            <Step n={3} title="Righe escluse dal food cost">
                <p>
                    Se una ricetta mostra un badge rosso
                    {" "}<strong>"N righe escluse dal food cost"</strong>, significa che alcune
                    righe non sono conteggiate (unità di misura incompatibile, ingrediente
                    cancellato o mismatch pz↔peso). Il tooltip elenca i nomi. Apri il piatto
                    per correggere le UM.
                </p>
            </Step>
            <Step n={4} title="Regola d'oro sulle dosi">
                <p>
                    Le ricette stanno sempre in <strong>grammi e millilitri</strong>, mai in
                    kg/l. Se inserisci 0,5 kg il sistema salva come 500 g automaticamente. Così
                    dosi piccole come "3 g di sale" restano precise e il food cost è affidabile.
                </p>
            </Step>
            <Step n={5} title="Eliminare ricette">
                <p>
                    Icona cestino su ogni card. C'è anche <strong>Cancella tutte le ricette</strong>
                    {" "}per svuotare il menu (conferma richiesta).
                </p>
            </Step>
        </Section>
    );
}

function CatalogSection() {
    return (
        <Section id="catalogo" icon={BookOpen} title="Catalogo globale di ricette" eyebrow="Ricette pronte">
            <p className="text-[14px] text-brand-ink/85">
                Il portale include un catalogo di oltre 240 ricette italiane tradizionali pronte
                da importare nel tuo menu: paste, secondi di carne e pesce, pizze, dolci,
                contorni, cocktail.
            </p>
            <Step n={1} title="Sfogliare il catalogo">
                <p>
                    In <strong>/recipes</strong> clicca <strong>Scegli dal catalogo</strong>. Si
                    apre un dialog con ricerca e filtro per categoria.
                </p>
            </Step>
            <Step n={2} title="Importare una ricetta">
                <p>
                    Tap <strong>Aggiungi</strong> sulla card: la ricetta viene copiata nel tuo
                    menu, le dosi vengono normalizzate in g/ml, e gli ingredienti vengono
                    automaticamente associati a quelli della tua dispensa (via match per nome).
                </p>
                <Callout tone="info" title="Ingredienti mancanti">
                    Se una ricetta del catalogo usa ingredienti che non hai in dispensa, vedrai
                    un badge "N ingredienti mancanti". Puoi importarla comunque (le righe
                    mancanti verranno marcate) oppure aggiungere prima gli ingredienti.
                </Callout>
            </Step>
            <Step n={3} title="Importa tutto">
                <p>
                    Con <strong>Importa tutte</strong> porti in un colpo solo l'intero catalogo
                    nel tuo menu. Il portale salta i duplicati (stesso nome) e produce un report
                    con gli ingredienti mancanti aggregati: sai subito cosa aggiungere alla
                    dispensa per sbloccare il maggior numero di ricette.
                </p>
            </Step>
        </Section>
    );
}

function RecipeAISection() {
    return (
        <Section id="ricetta-ai" icon={Sparkles} title="Ricetta dettagliata AI" eyebrow="Generazione con AI">
            <p className="text-[14px] text-brand-ink/85">
                Da ogni scheda piatto (esclusa la categoria Bevande) puoi chiedere all'AI di
                generare una <strong>ricetta dettagliata da chef italiano</strong>: descrizione,
                tempi, difficoltà, passaggi e consigli.
            </p>
            <Step n={1} title="Aprire il dialog">
                <p>
                    Clic su <strong>Ricetta dettagliata</strong> nella card del piatto. Se la
                    ricetta non è ancora stata generata vedi un placeholder con il pulsante
                    <strong> Genera con AI</strong>.
                </p>
            </Step>
            <Step n={2} title="Pizze: tipo di forno">
                <p>
                    Per le ricette nella categoria <strong>Pizze</strong> il dialog chiede il
                    tipo di forno (<strong>a legna</strong> oppure <strong>elettrico
                    domestico/professionale</strong>). L'AI adatta temperatura, tempo e tecnica
                    di cottura di conseguenza, e include la gestione completa dell'impasto
                    (idratazione, lievitazione 24-48 h, staglio, stesura a mano).
                </p>
            </Step>
            <Step n={3} title="Quote consumate">
                <p>
                    Ogni generazione consuma 1 ricetta AI dalla tua quota mensile (vedi{" "}
                    <a href="#billing" className="text-brand-navy underline font-semibold">Piani & quote</a>).
                    Il risultato viene salvato sul piatto: le successive aperture non
                    consumano altre quote, a meno che tu non clicchi <strong>Rigenera</strong>.
                </p>
            </Step>
            <Callout tone="warning" title="Tempi di attesa">
                La generazione impiega normalmente 10-30 secondi. Massimo 50 secondi, dopodiché
                scatta un timeout con messaggio chiaro.
            </Callout>
        </Section>
    );
}

function MenuImportSection() {
    return (
        <Section id="menu-import" icon={Upload} title="Import menu PDF (AI)" eyebrow="Creazione automatica ricette">
            <p className="text-[14px] text-brand-ink/85">
                Hai un menu già stampato o un PDF col listino del tuo ristorante? Il portale
                estrae automaticamente <strong>piatti, categorie, prezzi e ingredienti</strong>
                {" "}usando Gemini 2.5 Flash.
            </p>
            <Step n={1} title="Aprire il dialog">
                <p>
                    Su <strong>/recipes</strong> → pulsante <strong>Carica menu PDF</strong>.
                    Formati accettati: PDF, JPG, PNG, WEBP. Dimensione massima 15 MB.
                </p>
            </Step>
            <Step n={2} title="Upload e analisi">
                <p>
                    Trascina il file o clicca per scegliere. L'AI estrae i piatti con i loro
                    ingredienti per 1 porzione e in pochi secondi ti restituisce un riepilogo.
                </p>
            </Step>
            <Step n={3} title="Cosa viene creato">
                <ul className="space-y-1.5">
                    <Bullet>
                        <strong>Piatti</strong>: i duplicati (stesso nome) vengono saltati.
                    </Bullet>
                    <Bullet>
                        <strong>Ingredienti mancanti</strong>: quelli non presenti in dispensa
                        vengono creati automaticamente con costo 0€ e categoria "Dispensa".
                        Apri la scheda per aggiornarli.
                    </Bullet>
                    <Bullet>
                        <strong>Dosi sempre in g/ml</strong>: l'AI usa grammi e millilitri
                        anche per sale/aglio/pepe; kg/l non compaiono nelle ricette.
                    </Bullet>
                </ul>
            </Step>
            <Callout tone="info" title="Un solo caricamento per menu">
                Se riprocessi lo stesso menu, i piatti con lo stesso nome vengono saltati (no
                duplicati). Per rimpiazzare un piatto elimina prima quello esistente.
            </Callout>
        </Section>
    );
}

function PurchasesSection() {
    return (
        <Section id="acquisti" icon={ShoppingBasket} title="Acquisti & OCR fatture" eyebrow="Inserimento spese">
            <p className="text-[14px] text-brand-ink/85">
                Registrare gli acquisti aggiorna in automatico il costo medio ponderato degli
                ingredienti e alimenta la sezione Statistiche.
            </p>
            <Step n={1} title="Aggiungere un acquisto manuale">
                <p>
                    Su <strong>/purchases</strong> → <strong>Nuovo acquisto</strong>: scegli
                    l'ingrediente, quantità, costo totale o unitario, fornitore e data.
                </p>
            </Step>
            <Step n={2} title="OCR fatture e DDT (AI)">
                <p>
                    Pulsante <strong>Carica DDT/fattura</strong>: il portale estrae
                    automaticamente righe di acquisto da PDF / JPG / PNG. Riconosce quantità,
                    UM, costo unitario e fornitore. Tu rivedi, confermi o correggi.
                </p>
                <Callout tone="success" title="Cosa gestisce l'OCR">
                    Multi-pagina PDF, cartoni × bottiglie (es. "2 cartoni × 6 × 1.5L = 18 l"),
                    sconti in riga, più fornitori in un unico documento.
                </Callout>
            </Step>
            <Step n={3} title="Media ponderata automatica">
                <p>
                    Quando registri un acquisto, il costo unitario dell'ingrediente si aggiorna
                    così:
                </p>
                <div className="bg-brand-cream border border-brand-line rounded-md px-3 py-2 text-[13px] font-mono text-brand-ink/85">
                    nuovo_costo = (stock_esistente × vecchio_costo + qty × nuovo_costo) /
                    (stock_esistente + qty)
                </div>
                <p>
                    Se avevi 2 kg di pomodoro a 1,50 €/kg e ne compri 5 kg a 1,80 €/kg, il
                    nuovo costo medio sarà circa 1,71 €/kg.
                </p>
            </Step>
            <Step n={4} title="Storico acquisti">
                <p>
                    Puoi filtrare per periodo, ingrediente, fornitore, e modificare o cancellare
                    le righe (il costo medio viene ricalcolato).
                </p>
            </Step>
        </Section>
    );
}

function SuppliersSection() {
    return (
        <Section id="fornitori" icon={Truck} title="Fornitori & listini" eyebrow="Rete di acquisto">
            <p className="text-[14px] text-brand-ink/85">
                La pagina Fornitori è la tua rubrica di acquisto. Ogni fornitore può avere un
                listino di prezzi associati agli ingredienti della dispensa.
            </p>
            <Step n={1} title="Anagrafica fornitore">
                <p>
                    Nome commerciale, email, telefono, note (giorni consegna, ordine minimo,
                    referente). Questi dati finiscono anche nell'ordine stampabile.
                </p>
            </Step>
            <Step n={2} title="Listino prezzi">
                <p>
                    Per ogni fornitore puoi inserire il prezzo a cui vende ciascun ingrediente:
                    questo è il dato chiave che alimenta gli <strong>Ordini intelligenti</strong>.
                    Se più fornitori hanno lo stesso ingrediente a listino, il portale sceglie
                    il più conveniente.
                </p>
            </Step>
            <Step n={3} title="Fornitore TEST">
                <p>
                    Puoi generare un <strong>fornitore TEST</strong> con 260+ prezzi plausibili
                    per provare gli Ordini intelligenti senza inserire listini a mano.
                </p>
            </Step>
            <Callout tone="gold" title="Perché vale la pena inserire i listini">
                Senza listini il carrello ordini ti mostrerà tutto in "da associare a
                fornitore". Con i listini diventa uno <strong>split automatico</strong>: un
                clic e hai il preventivo completo pronto da mandare.
            </Callout>
        </Section>
    );
}

function OrdersSection() {
    return (
        <Section id="ordini" icon={PackageCheck} title="Ordini intelligenti" eyebrow="Pro feature">
            <div className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.16em] text-brand-gold">
                <Crown size={14} /> Richiede piano Pro (incluso nel trial iniziale)
            </div>
            <p className="text-[14px] text-brand-ink/85">
                Il <strong>carrello ordini persistente</strong> calcola da solo come suddividere
                un ordine tra più fornitori scegliendo, riga per riga, il prezzo più basso.
            </p>
            <Step n={1} title="Aggiungere al carrello">
                <ul className="space-y-1.5">
                    <Bullet>
                        <strong>Da /ingredients</strong>: pulsante <strong>Ordina</strong>
                        {" "}accanto a ogni ingrediente. Scegli quantità e UM, il sistema
                        aggiunge la riga.
                    </Bullet>
                    <Bullet>
                        <strong>Da /recipes</strong>: pulsante <strong>Ordina</strong> sulla
                        card piatto → chiedi quante porzioni stai preparando, il sistema
                        moltiplica le dosi per porzione e aggiunge al carrello tutti gli
                        ingredienti necessari.
                    </Bullet>
                    <Bullet>
                        <strong>Da /orders</strong>: pulsante <strong>Aggiungi voce</strong>
                        {" "}per scegliere un ingrediente dalla dispensa.
                    </Bullet>
                </ul>
            </Step>
            <Step n={2} title="Auto-accorpamento duplicati">
                <p>
                    Se aggiungi un ingrediente già presente nel carrello, le righe vengono
                    <strong>accorpate</strong> convertendo le unità (es. 2 kg + 500 g = 2,5 kg
                    totali). Compare un dialog che ti mostra
                    {" "}<code>precedente + aggiunta = totale</code>: puoi confermare il totale o
                    modificarlo (utile se avevi sbagliato l'ordine precedente).
                </p>
            </Step>
            <Step n={3} title="Modifica diretta della UM">
                <p>
                    Ogni riga del carrello ha pill cliccabili per cambiare l'unità (g, kg, ml,
                    l, pz). Utile per spezie e liquidi che preferisci ordinare in confezioni
                    diverse.
                </p>
            </Step>
            <Step n={4} title="Forzare il fornitore">
                <p>
                    Sotto a ogni riga c'è la tendina <strong>Fornitore</strong>:
                </p>
                <ul className="space-y-1.5">
                    <Bullet><strong>Auto (miglior prezzo)</strong>: default, lo sceglie il motore.</Bullet>
                    <Bullet>Fornitore X: forzi la riga su un fornitore specifico anche se non è il più economico.</Bullet>
                </ul>
            </Step>
            <Step n={5} title="Calcolare l'ordine">
                <p>
                    Pulsante <strong>Calcola ordine</strong> →
                </p>
                <ul className="space-y-1.5">
                    <Bullet>
                        <strong>Split per fornitore</strong>: ogni fornitore con le sue righe e
                        subtotale.
                    </Bullet>
                    <Bullet>
                        <strong>Lista acquisti da associare a fornitore</strong>: gli
                        ingredienti senza listino. Per ognuno puoi scegliere al volo un
                        fornitore e un prezzo; alla conferma la riga viene spostata nell'ordine.
                    </Bullet>
                    <Bullet>
                        <strong>Totale ordine</strong>: la somma complessiva.
                    </Bullet>
                </ul>
            </Step>
            <Step n={6} title="Stampa, copia e chiusura">
                <ul className="space-y-1.5">
                    <Bullet>
                        <strong>Copia</strong>: testo formattato ordine-per-fornitore negli
                        appunti, pronto da incollare in un'email.
                    </Bullet>
                    <Bullet>
                        <strong>Stampa</strong>: PDF multi-pagina A4 con header del tuo
                        ristorante, uno per fornitore + lista riepilogativa.
                    </Bullet>
                    <Bullet>
                        <strong>Ordine effettuato</strong>: svuota il carrello dopo aver
                        mandato l'ordine ai fornitori.
                    </Bullet>
                    <Bullet>
                        <strong>Cancella ordine</strong>: svuota il carrello senza conferme.
                    </Bullet>
                </ul>
            </Step>
            <Callout tone="gold" title="Il carrello resta salvato">
                Chiudi la tab, riprendilo dopo pranzo: il carrello è persistente in DB. Sparisce
                solo se premi "Ordine effettuato" o "Cancella ordine".
            </Callout>
        </Section>
    );
}

function MissingListSection() {
    return (
        <Section id="lista-mancanti" icon={ShoppingCart} title="Lista mancanti" eyebrow="Shopping list">
            <p className="text-[14px] text-brand-ink/85">
                Raccoglie automaticamente gli ingredienti che ti servirebbero ma non hai in
                dispensa. Si popola da:
            </p>
            <ul className="space-y-1.5">
                <Bullet>Import di ricette dal catalogo globale con ingredienti non ancora registrati.</Bullet>
                <Bullet>Voci aggiunte a mano dalla pagina dispensa.</Bullet>
            </ul>
            <p className="text-[14px] text-brand-ink/85">
                Puoi aggiornare quantità, stampare la lista, assegnare un fornitore o
                convertire direttamente una riga in ingrediente di dispensa con prezzo.
            </p>
        </Section>
    );
}

function StatsSection() {
    return (
        <Section id="statistiche" icon={BarChart3} title="Statistiche" eyebrow="Insight del ristorante">
            <p className="text-[14px] text-brand-ink/85">
                Tre viste in un'unica pagina, pensate per il cost controller.
            </p>
            <ul className="space-y-1.5">
                <Bullet>
                    <strong>Food cost per piatto</strong>: distribuzione e outlier. I piatti
                    sopra il 35% sono da rivedere (ridurre dose, cambiare fornitore o alzare
                    prezzo).
                </Bullet>
                <Bullet>
                    <strong>Spesa per categoria</strong>: quanto stai spendendo in carni, pesce,
                    frutta/verdura, latticini ecc. Se una categoria esplode sopra il trend, è
                    una red flag.
                </Bullet>
                <Bullet>
                    <strong>Top fornitori</strong>: i partner che ricevono la maggior parte del
                    tuo spending. Utile per negoziare sconti volume.
                </Bullet>
            </ul>
        </Section>
    );
}

function BillingSection() {
    return (
        <Section id="billing" icon={CreditCard} title="Piani & quote AI" eyebrow="Abbonamento">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="rounded-xl border border-brand-line bg-brand-cream p-4">
                    <div className="eyebrow text-brand-navy">Base · 29€/mese</div>
                    <ul className="mt-2 space-y-1 text-[13px] text-brand-ink/80">
                        <li>• Ingredienti, ricette, acquisti illimitati</li>
                        <li>• 30 fatture OCR / mese</li>
                        <li>• 2 import menu PDF / mese</li>
                        <li>• 5 ricette AI / mese</li>
                    </ul>
                </div>
                <div className="rounded-xl border border-brand-gold/50 bg-brand-gold/10 p-4 relative">
                    <div className="absolute top-3 right-3 text-[9px] uppercase tracking-[0.16em] font-bold text-brand-gold">
                        CONSIGLIATO
                    </div>
                    <div className="eyebrow text-brand-gold">Pro · 49€/mese</div>
                    <ul className="mt-2 space-y-1 text-[13px] text-brand-ink/80">
                        <li>• Tutto di Base +</li>
                        <li>• <strong>Ordini intelligenti</strong></li>
                        <li>• 60 fatture OCR / mese</li>
                        <li>• 5 import menu PDF / mese</li>
                        <li>• 15 ricette AI / mese</li>
                    </ul>
                </div>
            </div>
            <div className="rounded-xl border border-brand-line bg-paper p-4">
                <div className="flex items-center gap-2 mb-2">
                    <Zap size={16} className="text-brand-gold" />
                    <div className="font-display text-lg text-brand-ink">Ricarica top-up · 10€</div>
                </div>
                <p className="text-[13px] text-brand-ink/80">
                    Una tantum: <strong>80 fatture + 5 menu + 15 ricette AI</strong> extra. I
                    crediti non scadono e si sommano alle quote del piano. Ideale per mesi di
                    picco (es. cambio menu stagionale).
                </p>
            </div>
            <Step n={1} title="Come acquistare">
                <p>
                    Da <strong>/billing</strong> → scegli Base, Pro o Top-up → vieni
                    reindirizzato a <strong>Stripe Checkout</strong>. Pagamento con carta.
                    Dopo il pagamento tornerai sul portale con il piano già attivo.
                </p>
            </Step>
            <Step n={2} title="Controllare le quote residue">
                <p>
                    Il badge <strong>Quota</strong> sulle pagine <strong>/recipes</strong> e
                    {" "}<strong>/purchases</strong> mostra sempre i caricamenti rimasti questo
                    mese. Sui popup di upload vedi un banner dedicato che ti avverte se stai
                    per esaurire la quota.
                </p>
            </Step>
            <Step n={3} title="Cancellazione">
                <p>
                    Disattivi l'abbonamento dal portale clienti Stripe (linkato da
                    <strong> /billing</strong>). Manterrai accesso fino alla fine del periodo già pagato.
                </p>
            </Step>
            <Callout tone="info" title="Utenti con AI illimitata">
                Se l'admin ti ha attivato <strong>AI illimitata</strong> vedrai il simbolo
                {" "}<InfinityIcon className="inline" size={12} /> al posto del conteggio: upload
                e ricette AI non hanno limiti.
            </Callout>
        </Section>
    );
}

function ProfileSection() {
    return (
        <Section id="profilo" icon={User} title="Profilo" eyebrow="Account">
            <p className="text-[14px] text-brand-ink/85">
                Dalla pagina Profilo aggiorni i dati del ristorante (nome che appare sugli
                ordini stampabili), vedi lo stato dell'abbonamento e la cronologia fatture.
            </p>
            <ul className="space-y-1.5">
                <Bullet>
                    <strong>Nome del ristorante</strong>: compare nel PDF degli ordini, nelle
                    email dei fornitori e in alcuni report.
                </Bullet>
                <Bullet>
                    <strong>Avatar e dati Google</strong>: sincronizzati automaticamente dal
                    tuo account.
                </Bullet>
                <Bullet>
                    <strong>Logout</strong>: in basso a sinistra nella sidebar (o bottom-nav su
                    mobile).
                </Bullet>
            </ul>
        </Section>
    );
}

function CasesSection() {
    return (
        <Section id="casi-specifici" icon={AlertTriangle} title="Casi specifici" eyebrow="Problemi tipici risolti">
            <Case
                q="Il food cost di un piatto è troppo basso — mi sa che manca un ingrediente nel conto"
                a={
                    <>
                        Controlla il badge rosso <strong>"N righe escluse dal food cost"</strong>
                        {" "}sulla card. Significa che qualche riga non è conteggiata: tipicamente
                        un ingrediente in <code>pz</code> puntato a un ingrediente migrato a peso.
                        Vai su <Link to="/ingredients" className="text-brand-navy underline font-semibold">Ingredienti</Link>
                        {" "}e premi <strong>Correggi N a peso</strong>, poi apri il piatto e
                        verifica.
                    </>
                }
            />
            <Case
                q="Ho importato Carbonara dal catalogo ma le uova non contano nel food cost"
                a={
                    <>
                        Era un bug ormai risolto: ora il portale converte automaticamente
                        {" "}<code>1 pz uovo</code> → <code>55 g</code> al momento dell'import,
                        usando il peso medio. Se hai ricette importate <strong>prima</strong> del
                        fix, premi <strong>Correggi a peso</strong> su Ingredienti: lo stesso
                        processo sistema le ricette esistenti.
                    </>
                }
            />
            <Case
                q="L'OCR fattura ha sbagliato una quantità"
                a={
                    <>
                        Dopo l'upload il portale ti mostra il riepilogo delle righe estratte
                        {" "}<em>prima</em> di confermarle: puoi correggere quantità, UM e costo
                        unitario. Una volta confermato, gli errori si risolvono da
                        {" "}<Link to="/purchases" className="text-brand-navy underline font-semibold">/purchases</Link>
                        {" "}aprendo il singolo acquisto.
                    </>
                }
            />
            <Case
                q="L'import menu PDF ha creato ingredienti generici a costo zero"
                a={
                    <>
                        È il comportamento atteso: quando un piatto cita un ingrediente non
                        ancora in dispensa, il portale lo crea automaticamente con cost_per_unit=0
                        e categoria "Dispensa", così le ricette restano coerenti. Apri
                        {" "}<Link to="/ingredients" className="text-brand-navy underline font-semibold">/ingredients</Link>
                        {" "}e imposta prezzo corretto, categoria, scarto.
                    </>
                }
            />
            <Case
                q="Nel carrello ho 2 righe dello stesso ingrediente"
                a={
                    <>
                        Non succede più: il portale <strong>accorpa automaticamente</strong> le
                        righe duplicate convertendo le unità e chiede la quantità totale.
                        Se resta traccia di vecchi ordini duplicati, puoi unirli a mano con
                        l'icona cestino e ri-aggiungere.
                    </>
                }
            />
            <Case
                q="Il fornitore che voglio non compare nello split automatico"
                a={
                    <>
                        Significa che non ha l'ingrediente a listino. Vai su
                        {" "}<Link to="/suppliers" className="text-brand-navy underline font-semibold">/suppliers</Link>
                        {" "}e aggiungilo, oppure usa la tendina sulla riga del carrello per
                        forzarlo.
                    </>
                }
            />
            <Case
                q="Ho esaurito le ricette AI del mese"
                a={
                    <>
                        Due strade:
                        <br />1) Passa al piano Pro (15 ricette AI/mese invece di 5).
                        <br />2) Acquista il top-up da 10€: 15 ricette AI extra + 80 fatture + 5
                        menu. I crediti non scadono.
                    </>
                }
            />
            <Case
                q="Voglio un piatto con food cost sotto 25% ma non ci arrivo"
                a={
                    <>
                        Due leve:
                        <br />• <strong>Dose</strong>: riduci la porzione dell'ingrediente più
                        costoso (controlla il <em>breakdown</em> nella scheda piatto).
                        <br />• <strong>Fornitore</strong>: vai su /suppliers, aggiungi un
                        listino alternativo e lascia che Ordini intelligenti scelga il più
                        conveniente.
                    </>
                }
            />
            <Case
                q="Ho più sedi e voglio gestirle separatamente"
                a={
                    <>
                        Oggi ogni account è un ristorante separato. Per più sedi: crea un
                        account Google diverso per ogni sede (trial 5 giorni + piano Base/Pro
                        autonomo). Consolidamento multi-sede è in roadmap.
                    </>
                }
            />
        </Section>
    );
}

function Case({ q, a }) {
    return (
        <div className="rounded-xl border border-brand-line bg-brand-cream/70 p-4">
            <div className="flex items-start gap-2.5 mb-2">
                <HelpCircle size={16} className="text-brand-gold shrink-0 mt-0.5" />
                <div className="font-semibold text-brand-ink text-[14px]">{q}</div>
            </div>
            <div className="pl-[26px] text-[13px] text-brand-ink/80 leading-relaxed">{a}</div>
        </div>
    );
}

function FAQSection() {
    return (
        <Section id="faq" icon={HelpCircle} title="FAQ — Domande frequenti" eyebrow="Risposte rapide">
            <FAQ
                q="I miei dati sono al sicuro?"
                a="Sì. Ogni account ha il proprio spazio isolato: nessuna query legge dati di altri utenti. Le comunicazioni sono HTTPS, i pagamenti Stripe sono PCI-DSS."
            />
            <FAQ
                q="Posso usare il portale da mobile?"
                a="Sì. L'interfaccia è completamente responsive: sidebar su desktop, bottom-nav su mobile con le stesse funzioni."
            />
            <FAQ
                q="Quanto tempo ci metto a configurare il ristorante?"
                a="15-30 minuti: importi il menu da PDF (o scegli dal catalogo), sistemi i prezzi degli ingredienti dalla dispensa pre-caricata, e sei pronto."
            />
            <FAQ
                q="Funziona senza connessione internet?"
                a="No: le operazioni richiedono il backend. Dashboard e pagine già aperte restano visibili, ma per salvare serve connessione."
            />
            <FAQ
                q="Posso esportare i dati?"
                a="La stampa PDF degli ordini e il pulsante 'Copia' degli ordini sono già disponibili. Export Excel completo (ingredienti, ricette, acquisti) è in roadmap."
            />
            <FAQ
                q="Chi mi può aiutare se ho problemi?"
                a="Supporto prioritario via email per utenti Pro. Per bug reprodubili invia screenshot + passi per riprodurlo."
            />
            <FAQ
                q="Quanto è affidabile l'AI?"
                a="Gemini 2.5 Flash è molto solido per l'italiano. L'import menu ha ~95% di accuratezza sui PDF nitidi; la ricetta dettagliata è pensata per uno chef che sa leggere tempi e tecniche — non sostituisce l'esperienza in cucina."
            />
        </Section>
    );
}

function FAQ({ q, a }) {
    return (
        <details className="group rounded-xl border border-brand-line bg-paper open:shadow-card transition">
            <summary className="flex items-start gap-3 cursor-pointer list-none px-4 py-3 select-none">
                <FileText size={15} className="text-brand-gold shrink-0 mt-0.5" />
                <span className="flex-1 text-[14px] font-semibold text-brand-ink">{q}</span>
                <span className="text-brand-muted text-lg leading-none shrink-0 group-open:rotate-45 transition">
                    +
                </span>
            </summary>
            <div className="px-4 pb-4 pl-[42px] text-[13px] text-brand-ink/80 leading-relaxed">
                {a}
            </div>
        </details>
    );
}

// Utility: icona Scale/Layers usati in altre sezioni (assicurarsi export)
// eslint-disable-next-line no-unused-vars
const _icons_tree_shake_guard = { Scale, Layers };
